import { useEffect, useState } from 'react';
import { firmService, FirmMembership } from '../lib/firmService';
import { useAuth } from '../contexts/AuthContext';

/**
 * Resolves whether the current user is a firm member. Drives both the
 * /firm route guard and the conditional nav link. A user is a "firm user"
 * iff they have a firm_members row — profession is irrelevant.
 */
export function useFirm() {
  const { user, loading: authLoading } = useAuth();
  const [firm, setFirm] = useState<FirmMembership | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    if (authLoading) return;
    if (!user) {
      setFirm(null);
      setError(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    firmService
      .getMyFirm()
      .then((f) => {
        if (active) {
          setFirm(f);
          setLoading(false);
        }
      })
      .catch(() => {
        // A thrown error means the lookup failed (not that the user isn't a
        // member). Surface it so callers don't false-bounce a real firm member.
        if (active) {
          setFirm(null);
          setError(true);
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [user, authLoading]);

  return { firm, loading, error, isFirmMember: !!firm };
}
